import { getFormattedDate } from '@app/utils/date'
import { dateFormat } from '@app/constants'

import { DecodedValueMap } from 'use-query-params'

import { QueryParamConfigMap } from 'serialize-query-params'

import { getPreviewPrice } from './utils'

import type { TableData } from '@app/types/table'
import type { ProcessingResultsDataSource } from '@app/pages/ProcessingResultsPage/types'
import type { ResponseDto } from '@app/api/dtos'
import type { ResultsDto } from '@app/api/routes/monitoringTasks/dtos'
import type { GetResultsParams } from '@app/api/routes/monitoringTasks/types'

export const adaptProcessingResultsTableData = (
  resultsResponse: ResponseDto<ResultsDto>
): TableData<ProcessingResultsDataSource> => {
  const { count, results } = resultsResponse

  const dataSource: ProcessingResultsDataSource[] = results.map(dto => ({
    key: dto.id,
    monitoringType: dto.monitoringTypeName,
    name: dto.productName,
    productCode: dto.productCode,
    cluster: dto.clusterName,
    store: dto.matchedStore?.name ?? null,
    competitor: dto.competitor.text,
    purchasePrice: dto.purchasePrice,
    storeRegularPrice: dto.storeRegularPrice,
    regularPriceCompetitor: getPreviewPrice(dto, 'regularPrice'),
    promoPriceCompetitor: getPreviewPrice(dto, 'promoPrice'),
    storePromoPrice: dto.storePromoPrice,
    priceDifference: dto.priceDifference,
    note: dto.note,
    abnormalState: dto.abnormalState,
    monitoringInstruction: dto.howToMonitor,
    date: getFormattedDate(dto?.collectedDateAt, dateFormat.DD_MM_YYYY),
    promotionName: dto.task?.promotionNumber ?? null,
    promoDateFrom: getFormattedDate(dto.task?.promoDateFrom, dateFormat.DD_MM_YYYY),
    promoDateTo: getFormattedDate(dto.task?.promoDateTo, dateFormat.DD_MM_YYYY),
    startPromoDateAt: getFormattedDate(dto.startPromoDateAt, dateFormat.DD_MM_YYYY),
    endPromoDateAt: getFormattedDate(dto.endPromoDateAt, dateFormat.DD_MM_YYYY),
    discount: dto.discount,
    discountMechanics: dto.discountMechanics,
    productRating: dto.productRating
  }))

  return { count, dataSource }
}

export const adaptGetResultsParams = (queryParams: DecodedValueMap<QueryParamConfigMap>): GetResultsParams => {
  return {
    monitoringKindIds: queryParams.monitoringKindIds,
    week: queryParams.week,
    collectedDateAtAfter: queryParams.collectedDateAtAfter,
    collectedDateAtBefore: queryParams.collectedDateAtBefore,
    monitoringTypeIds: queryParams.monitoringTypeIds,
    storeIds: queryParams.storeIds,
    clusterIds: queryParams.clusterIds,
    salesOrganizationIds: queryParams.salesOrganizationIds,
    competitorIds: queryParams.competitorIds,
    competitorNetworkIds: queryParams.competitorNetworkIds,
    priceType: queryParams.priceType,
    priceDifferenceFrom: queryParams.priceDifferenceFrom,
    priceDifferenceTo: queryParams.priceDifferenceTo,
    excludeRangePriceDifference: queryParams.excludeRangePriceDifference,
    productCodes: queryParams.productCodes,
    productName: queryParams.productName,
    purchasingGroupIds: queryParams.purchasingGroupIds,
    trendIds: queryParams.trendIds,
    commodityGroupIds: queryParams.commodityGroupIds,
    isMatchingStatus: queryParams.isMatchingStatus,
    commentsByUsers: queryParams.commentsByUsers,
    isCHTM: queryParams.isCHTM,
    isEPP: queryParams.isEPP,
    isInfo: queryParams.isInfo,
    isShortKvi: queryParams.isShortKvi,
    isTop1: queryParams.isTop1,
    tempMockBigMedia: queryParams.tempMockBigMedia,
    startPromoDateAt: queryParams.startPromoDateAt,
    endPromoDateAt: queryParams.endPromoDateAt,
    maxDiscount: queryParams.maxDiscount,
    minDiscount: queryParams.minDiscount
  }
}
